import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { BankIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';

import { formatAccountSubtype } from '../../utils/accounts';
import SyncStatus from '../SyncStatus';

import type { FinancialAccountSubtype } from '../../__generated__/globalTypes';

type SyncStatusPlacement = 'inline' | 'stacked-below';

interface IAccountMetadataProps {
  className?: string;
  subtype?: FinancialAccountSubtype;
  bankName: string;
  mask: string;
  lastSyncedAt?: string;
  /**
   * How to place the sync status pill on small viewports.
   *
   * - `'inline'` (default) — at sm+ shows the full inline meta with sync at
   *   the end; at `<sm` shows only the sync pill if present, otherwise
   *   `bank | ••mask`. Used by `AccountRow`, `DashboardAccountsWidget`, and
   *   any other compact-row consumer.
   * - `'stacked-below'` — at `<md` renders `subtype | bank | ••mask` on one
   *   row and the sync pill on a separate row below. At `md+` behaves
   *   identically to `'inline'`. Used by `AccountPaneHeader` to match the
   *   Figma Account "Section Header" layout.
   */
  syncStatusPlacement?: SyncStatusPlacement;
}

/** Inline metadata line for a financial account: bank name, mask, optional subtype and sync status. */
const AccountMetadata = ({
  className,
  subtype,
  bankName,
  mask,
  lastSyncedAt,
  syncStatusPlacement = 'inline',
}: IAccountMetadataProps) => {
  const isSmOrUp = useBreakpoint('sm');
  const isMdOrUp = useBreakpoint('md');
  const accountSubtypeLabel = subtype ? formatAccountSubtype(subtype) : '';

  const separator = <span className="text-quaternary shrink-0">|</span>;

  const syncStatus = lastSyncedAt ? (
    <SyncStatus className="shrink-0 px-1" lastSyncedAt={lastSyncedAt} addPrefix={true} />
  ) : null;

  // Bank icon prefixes the meta row only in the pane-header context
  // (`stacked-below`). List-row consumers like `AccountRow` already render
  // the bank logo as their avatar, so duplicating it in the meta line would
  // be visually noisy.
  const isPaneHeaderContext = syncStatusPlacement === 'stacked-below';
  const subtypeAndBank = (
    <>
      {isPaneHeaderContext && <BankIcon size={18} className="shrink-0" />}
      {accountSubtypeLabel && <span className="shrink-0">{accountSubtypeLabel}</span>}
      {accountSubtypeLabel && separator}
      <span className="min-w-0 truncate">{bankName}</span>
    </>
  );

  // Stacked-below at <md: meta row + sync row in a vertical stack.
  if (syncStatusPlacement === 'stacked-below' && !isMdOrUp) {
    return (
      <div className={cx('text-tertiary flex min-w-0 max-w-full flex-col gap-1 font-normal', className)}>
        <div className="flex min-w-0 max-w-full items-center gap-1">
          {subtypeAndBank}
          {separator}
          <span className="shrink-0">••{mask}</span>
        </div>
        {syncStatus && <div className="flex min-w-0 max-w-full items-center">{syncStatus}</div>}
      </div>
    );
  }

  // Inline (default): full meta at sm+, fallback to sync-only or bank+mask at <sm.
  const bankAndMask = (
    <>
      <span className="min-w-0 truncate">{bankName}</span>
      {separator}
      <span className="shrink-0">••{mask}</span>
    </>
  );

  const content = isSmOrUp ? (
    <>
      {subtypeAndBank}
      {separator}
      <span className="shrink-0">••{mask}</span>
      {lastSyncedAt && separator}
      {syncStatus}
    </>
  ) : lastSyncedAt ? (
    syncStatus
  ) : (
    bankAndMask
  );

  return (
    <div className={cx('text-tertiary flex min-w-0 max-w-full items-center gap-1 font-normal', className)}>
      {content}
    </div>
  );
};

export default AccountMetadata;
